import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import UiTable from "../../../../components/table/Table.component";
import { TableStyle } from "../../../../models/components/table/table.model";
import { TableColumnTemplate } from "../../../../models/components/table/table-column.model";
import UiButton from "../../../../components/button/Button";
import { ParticipantAlertThresholdRow } from "../../../../models/entities/participant-alert-threshold.model";
import { BiometricUnit } from "../../../../models/entities/biometrics.model";
import { ParticipantAlertThresholdUpdateBody } from "../../../../models/endpoints/participant-alert-thresholds-endpoint.model";

function getUnit(measurementType: string): string {
	return measurementType === "BLOOD_PRESSURE"
		? BiometricUnit.bloodPressure
		: BiometricUnit.glucose;
}

/** Parse "180/120" into { systolic, diastolic }. Returns nulls if invalid. */
function parseBloodPressure(value: string): { systolic: number | null; diastolic: number | null } {
	const parts = value
		.trim()
		.split("/")
		.map((p) => p.trim());
	const sys = parts[0] ? Number(parts[0]) : null;
	const dia = parts[1] ? Number(parts[1]) : null;
	return {
		systolic: sys != null && !Number.isNaN(sys) ? sys : null,
		diastolic: dia != null && !Number.isNaN(dia) ? dia : null,
	};
}

/** Format systolic/diastolic to "180/120". */
function formatBloodPressure(systolic: number | null, diastolic: number | null): string {
	if (systolic == null && diastolic == null) return "";
	if (systolic != null && diastolic != null) return `${systolic}/${diastolic}`;
	if (systolic != null) return `${systolic}/`;
	return `/${diastolic}`;
}

interface AlertValuesTableProps {
	rows: ParticipantAlertThresholdRow[];
	onUpdate: (body: ParticipantAlertThresholdUpdateBody) => Promise<void>;
	isUpdating: boolean;
}

interface EditForm {
	criticallyHigh: string;
	criticallyLow: string;
}

const AlertValuesTable = ({ rows, onUpdate, isUpdating }: AlertValuesTableProps) => {
	const { t } = useTranslation("common");
	const [editingRow, setEditingRow] = useState<ParticipantAlertThresholdRow | null>(null);
	const [editForm, setEditForm] = useState<EditForm>({ criticallyHigh: "", criticallyLow: "" });

	const isEditing = (row: ParticipantAlertThresholdRow) =>
		editingRow?.measurementType === row.measurementType;

	const handleStartEdit = (row: ParticipantAlertThresholdRow) => {
		setEditingRow(row);
		setEditForm({ criticallyHigh: row.criticallyHigh, criticallyLow: row.criticallyLow });
	};

	const handleCancel = () => {
		setEditingRow(null);
		setEditForm({ criticallyHigh: "", criticallyLow: "" });
	};

	const handleSave = async () => {
		if (!editingRow) return;
		await onUpdate({
			ids: editingRow.ids,
			measurementType: editingRow.measurementType,
			criticallyHigh: editForm.criticallyHigh.trim() || undefined,
			criticallyLow: editForm.criticallyLow.trim() || undefined,
		});
		setEditingRow(null);
		setEditForm({ criticallyHigh: "", criticallyLow: "" });
	};

	return (
		<div className="mt-4">
			<h3 className="text-bold fs-5 mb-2">
				{t("PARTICIPANT.BIOMETRICS.ALERT_VALUES.TITLE")}
			</h3>
			<UiTable
				dataKey="measurementType"
				type={TableStyle.GREEN}
				tableClassName="alert-values-table"
				useAsCard={false}
				// Cells render inline-edit inputs driven by external state (editingRow/editForm),
				// which PrimeReact's BodyCell memo doesn't track — disable it so they redraw on edit.
				cellMemo={false}
				rowClassName={(row: ParticipantAlertThresholdRow) =>
					isEditing(row) ? "inline-form alert-values-form" : ""
				}
				columns={[
					{
						field: "measurement",
						title: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.MEASUREMENT",
						sortable: false,
						style: { width: "40%" },
						template: TableColumnTemplate.CUSTOM,
						templateOptions: {
							customTemplate: (row: ParticipantAlertThresholdRow) => (
								<span className="text-bold">
									{t(row.measurementLabelKey)}
									{row.isCustom && (
										<span className="text-color-secondary ml-1">
											{t("PARTICIPANT.BIOMETRICS.ALERT_VALUES.CUSTOM_LABEL")}
										</span>
									)}
								</span>
							),
						},
					},
					{
						field: "criticallyHigh",
						title: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.CRITICALLY_HIGH",
						sortable: false,
						style: { width: "25%" },
						template: TableColumnTemplate.CUSTOM,
						templateOptions: {
							customTemplate: (row: ParticipantAlertThresholdRow) => {
								const unit = getUnit(row.measurementType);
								if (!isEditing(row))
									return (
										<span className="text">
											{row.criticallyHigh}
											{unit && <span className="unit ml-2"> {unit}</span>}
										</span>
									);
								if (row.measurementType === "BLOOD_PRESSURE") {
									const { systolic, diastolic } = parseBloodPressure(
										editForm.criticallyHigh
									);
									return (
										<div className="input-unit flex align-items-center flex-nowrap">
											<InputNumber
												value={systolic}
												onValueChange={(e) =>
													setEditForm((prev) => ({
														...prev,
														criticallyHigh: formatBloodPressure(
															e.value ?? null,
															diastolic
														),
													}))
												}
												disabled={isUpdating}
												max={999}
												className="input-width field-3"
											/>
											<span className="input-separator mb-0">/</span>
											<InputNumber
												value={diastolic}
												onValueChange={(e) =>
													setEditForm((prev) => ({
														...prev,
														criticallyHigh: formatBloodPressure(
															systolic,
															e.value ?? null
														),
													}))
												}
												disabled={isUpdating}
												max={999}
												className="input-width field-3"
											/>
											{unit && <span className="unit ml-2"> {unit}</span>}
										</div>
									);
								}
								return (
									<div className="input-unit flex align-items-center flex-nowrap">
										<InputText
											value={editForm.criticallyHigh}
											onChange={(e) =>
												setEditForm((prev) => ({
													...prev,
													criticallyHigh: e.target.value ?? "",
												}))
											}
											disabled={isUpdating}
											className="input-width flex-1"
										/>
										{unit && <span className="unit ml-2"> {unit}</span>}
									</div>
								);
							},
						},
					},
					{
						field: "criticallyLow",
						title: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.CRITICALLY_LOW",
						sortable: false,
						style: { width: "25%" },
						template: TableColumnTemplate.CUSTOM,
						templateOptions: {
							customTemplate: (row: ParticipantAlertThresholdRow) => {
								const unit = getUnit(row.measurementType);
								if (!isEditing(row))
									return (
										<span className="text">
											{row.criticallyLow}
											{unit && <span className="unit ml-2"> {unit}</span>}
										</span>
									);
								if (row.measurementType === "BLOOD_PRESSURE") {
									const { systolic, diastolic } = parseBloodPressure(
										editForm.criticallyLow
									);
									return (
										<div className="input-unit flex align-items-center flex-nowrap">
											<InputNumber
												value={systolic}
												onValueChange={(e) =>
													setEditForm((prev) => ({
														...prev,
														criticallyLow: formatBloodPressure(
															e.value ?? null,
															diastolic
														),
													}))
												}
												disabled={isUpdating}
												max={999}
												className="input-width field-3"
											/>
											<span className="input-separator mb-0">/</span>
											<InputNumber
												value={diastolic}
												onValueChange={(e) =>
													setEditForm((prev) => ({
														...prev,
														criticallyLow: formatBloodPressure(
															systolic,
															e.value ?? null
														),
													}))
												}
												disabled={isUpdating}
												max={999}
												className="input-width field-3"
											/>
											{unit && <span className="unit ml-2"> {unit}</span>}
										</div>
									);
								}
								return (
									<div className="input-unit flex align-items-center flex-nowrap">
										<InputText
											value={editForm.criticallyLow}
											onChange={(e) =>
												setEditForm((prev) => ({
													...prev,
													criticallyLow: e.target.value ?? "",
												}))
											}
											disabled={isUpdating}
											className="input-width flex-1"
										/>
										{unit && <span className="unit ml-2">{unit}</span>}
									</div>
								);
							},
						},
					},
					{
						field: "edit",
						sortable: false,
						alignment: "right",
						style: { width: "150px" },
						template: TableColumnTemplate.CUSTOM,
						templateOptions: {
							customTemplate: (row: ParticipantAlertThresholdRow) => {
								if (isEditing(row)) {
									return (
										<>
											<UiButton
												type="button"
												className="p-button-sm p-button-outlined p-button-rounded"
												label="PARTICIPANT.BIOMETRICS.ALERT_VALUES.CANCEL"
												onClick={handleCancel}
												disabled={isUpdating}
											/>
											<UiButton
												type="button"
												className="p-button-sm p-button-rounded"
												label={
													isUpdating
														? "UI_COMPONENTS.BUTTONS.SAVING"
														: "PARTICIPANT.BIOMETRICS.ALERT_VALUES.SAVE"
												}
												loading={isUpdating}
												disabled={isUpdating}
												onClick={handleSave}
											/>
										</>
									);
								}
								return (
									<UiButton
										type="button"
										icon="pi pi-pencil"
										className="p-button-text p-button-rounded p-button-sm"
										onClick={() => handleStartEdit(row)}
										disabled={isUpdating}
										aria-label={t("PARTICIPANT.BIOMETRICS.LIST.edit")}
									/>
								);
							},
						},
					},
				]}
				value={rows}
				paginator={false}
				pagination={{
					first: 0,
					rows: 10,
					page: 0,
					sortField: "measurementType",
					sortOrder: 1,
					search: undefined,
					filters: undefined,
				}}
			/>
		</div>
	);
};

export default AlertValuesTable;
